import {
  ItemRecipientType,
  ItemSubjectType,
  ResponseSubject,
  ResponseSubjectType,
  User,
} from "../../types";

export default function isCompatible(
  responseSubjectType: ResponseSubjectType,
  itemSubjectType: ItemSubjectType,
  itemRecipientType?: ItemRecipientType,
  author?: User,
  isAuthorTeamLead?: boolean,
  responseSubject?: ResponseSubject,
): boolean {
  // General feedback is always compatible with general questions
  if (responseSubjectType === ResponseSubjectType.General) {
    return itemSubjectType === ItemSubjectType.General;
  }

  // Instructor results "Team instructor ratings" view: show team-instructor questions
  if (responseSubjectType === ResponseSubjectType.TeamInstructors) {
    return itemSubjectType === ItemSubjectType.TeamInstructors;
  }

  if (!responseSubject || !itemRecipientType) {
    switch (itemSubjectType) {
      case ItemSubjectType.PeerSelf:
      case ItemSubjectType.PeerOnly:
      case ItemSubjectType.LeadSelf:
      case ItemSubjectType.LeadOnly:
      case ItemSubjectType.NonLeadSelf:
      case ItemSubjectType.NonLeadOnly:
        return true;
      case ItemSubjectType.TeamInstructors:
        // For TeamInstructors, we need the responseSubject to check the subject user's role
        // If we don't have the responseSubject, we can't determine compatibility
        return false;
      case ItemSubjectType.TeamLearners:
        // TeamLearners has been consolidated into PeerSelf
        // Treat it the same as PeerSelf for backward compatibility
        return true;
      default:
        return false;
    }
  }

  const isLead = responseSubject.isTeamLead;
  const isSelf = responseSubject.subjectUserId === author?.id;
  const isAuthorInstructor = author?.isInstructor === true;

  switch (itemRecipientType) {
    case ItemRecipientType.TeamLeaders:
      if (!isAuthorTeamLead) {
        return false;
      }
      break;
    case ItemRecipientType.NonLeaders:
      if (isAuthorTeamLead) {
        return false;
      }
      // NonLeaders targets non-lead students only; instructors must also be excluded
      if (isAuthorInstructor) {
        return false;
      }
      break;
    case ItemRecipientType.TeamInstructors:
      // Only show to instructors - exclude if author is not an instructor
      if (!isAuthorInstructor) {
        return false;
      }
      break;
    case ItemRecipientType.TeamMembers:
      // Team members questions are for students (learners) only
      // Exclude if author is an instructor (instructors should use TeamInstructors questions)
      if (isAuthorInstructor) {
        return false;
      }
      break;
    case ItemRecipientType.TeamLearners:
      // TeamLearners has been consolidated into TeamMembers
      // Treat it the same as TeamMembers for backward compatibility
      if (isAuthorInstructor) {
        return false;
      }
      break;
    default:
      break;
  }

  switch (itemSubjectType) {
    case ItemSubjectType.PeerSelf:
      // Only show peer/self questions if the subject user is NOT an instructor
      // Instructors should only see TEAM_INSTRUCTORS_QUESTION questions
      return responseSubject.subjectUserIsInstructor !== true;
    case ItemSubjectType.PeerOnly:
      // Only show peer-only questions if the subject user is NOT an instructor
      if (responseSubject.subjectUserIsInstructor === true) {
        return false;
      }
      return !isSelf;
    case ItemSubjectType.LeadSelf:
      // Only show lead self-evaluation questions to leads evaluating themselves
      // But exclude if the subject user is an instructor
      if (responseSubject.subjectUserIsInstructor === true) {
        return false;
      }
      return isLead;
    case ItemSubjectType.LeadOnly:
      // Only show lead evaluation questions to non-leads evaluating leads
      // But exclude if the subject user is an instructor
      if (responseSubject.subjectUserIsInstructor === true) {
        return false;
      }
      return isLead && !isSelf;
    case ItemSubjectType.NonLeadSelf:
      // Only show non-lead self-evaluation questions to non-leads evaluating themselves
      // But exclude if the subject user is an instructor
      if (responseSubject.subjectUserIsInstructor === true) {
        return false;
      }
      return !isLead;
    case ItemSubjectType.NonLeadOnly:
      // Only show non-lead evaluation questions to leads/non-leads evaluating non-leads
      // But exclude if the subject user is an instructor
      if (responseSubject.subjectUserIsInstructor === true) {
        return false;
      }
      return !isLead && !isSelf;
    case ItemSubjectType.TeamInstructors:
      // Only show instructor evaluation questions when the subject user is an instructor
      if (responseSubjectType !== ResponseSubjectType.Peer) {
        return false;
      }
      // Check if subject user is an instructor using the embedded field
      return responseSubject.subjectUserIsInstructor === true;
    case ItemSubjectType.TeamLearners:
      // TeamLearners has been consolidated into PeerSelf
      // Treat it the same as PeerSelf for backward compatibility
      return true;
    default:
      return false;
  }
}
